import { motion } from 'framer-motion'
import { MESSAGE_ROLES, MESSAGE_TYPES } from '../../utils'
import CategorySelectionMessage from './CategorySelectionMessage'
import CompanyProfileAdoptionMessage from './CompanyProfileAdoptionMessage'
import CtaMessage from './CtaMessage'
import CulturalFitDimensionMatchMessage from './CulturalFitDimensionMatchMessage'
import CultureDetailsMessage from './CultureDetailsMessage'
import DimensionMatchMessage from './DimensionMatchMessage'
import DimensionPreviewMessage from './DimensionPreviewMessage'
import FeaturesMessage from './FeatureMessage'
import InterestsDetailsMessage from './InterestsDetailsMessage'
import InviteDemoTalentMessage from './InviteDemoTalentMessage'
import MatchingInsightsMessage from './MatchingInsightsMessage'
import NameInputMessage from './NameInputMessage'
import NextStepsPromptMessage from './NextStepsPromptMessage'
import OptionMessage from './OptionMessage'
import PersonalityDetailsMessage from './PersonalityDetailsMessage'
import RiasecDimensionMatchMessage from './RiasecDimensionMatchMessage'
import SkillsDetailsMessage from './SkillsDetailsMessage'
import SystemMessage from './SystemMessage'
import TalentMatchSuggestions from './TalentMatchSuggestions'
import VideoMessage from './VideoMessage'

const MOTION_ANIMATION_PROPS = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 20 },
  transition: { duration: 0.3, ease: 'easeOut' },
  layout: true
}

export default function MessageRenderer({ message, index }) {
  const {
    id,
    title,
    description,
    role,
    type,
    options,
    cta,
    onClick,
    withQrCode,
    imageSrc,
    dimensionMatchMessageProps,
    talentMatchSuggestionsProps,
    nextStepsPromptMessageProps,
    companyProfileAdoptionProps,
    culturalFitDimensionMatchMessageProps,
    riasecFitDimensionMatchMessageProps,
    skillsDetailsProps,
    cultureDetailsProps,
    interestsDetailsProps,
    personalityDetailsProps,
    matchingInsightsProps,
    nameInputProps,
    categorySelectionProps,
    videoMessageProps,
    featureProps,
    className = ''
  } = message

  const getMessageComponent = () => {
    if (type === MESSAGE_TYPES.VIDEO && role === MESSAGE_ROLES.ASSISTANT) {
      return <VideoMessage {...videoMessageProps} />
    }

    if (type === MESSAGE_TYPES.FEATURE && role === MESSAGE_ROLES.ASSISTANT) {
      return <FeaturesMessage {...featureProps} />
    }

    if (
      type === MESSAGE_TYPES.MATCHING_INSIGHTS &&
      role === MESSAGE_ROLES.ASSISTANT
    ) {
      return <MatchingInsightsMessage {...matchingInsightsProps} />
    }

    if (
      type === MESSAGE_TYPES.PERSONALITY_DETAILS &&
      role === MESSAGE_ROLES.ASSISTANT
    ) {
      return <PersonalityDetailsMessage {...personalityDetailsProps} />
    }

    if (
      type === MESSAGE_TYPES.INTERESTS_DETAILS &&
      role === MESSAGE_ROLES.ASSISTANT
    ) {
      return <InterestsDetailsMessage {...interestsDetailsProps} />
    }

    if (
      type === MESSAGE_TYPES.CULTURE_FIT_DETAILS &&
      role === MESSAGE_ROLES.ASSISTANT
    ) {
      return <CultureDetailsMessage {...cultureDetailsProps} />
    }

    if (
      type === MESSAGE_TYPES.SKILLS_DETAILS &&
      role === MESSAGE_ROLES.ASSISTANT
    ) {
      return <SkillsDetailsMessage {...skillsDetailsProps} />
    }

    if (
      type === MESSAGE_TYPES.RIASEC_FIT_CAREER_TALENT &&
      role === MESSAGE_ROLES.ASSISTANT
    ) {
      return (
        <RiasecDimensionMatchMessage {...riasecFitDimensionMatchMessageProps} />
      )
    }

    if (
      type === MESSAGE_TYPES.CULTURAL_FIT_CAREER_TALENT &&
      role === MESSAGE_ROLES.ASSISTANT
    ) {
      return (
        <CulturalFitDimensionMatchMessage
          {...culturalFitDimensionMatchMessageProps}
        />
      )
    }

    if (
      type === MESSAGE_TYPES.TALENT_MATCH_SUGGESTIONS &&
      role === MESSAGE_ROLES.ASSISTANT
    ) {
      return <TalentMatchSuggestions {...talentMatchSuggestionsProps} />
    }

    if (
      type === MESSAGE_TYPES.NEXT_STEPS_PROMPT &&
      role === MESSAGE_ROLES.ASSISTANT
    ) {
      return <NextStepsPromptMessage {...nextStepsPromptMessageProps} />
    }

    if (
      type === MESSAGE_TYPES.DIMENSION_CAREER_TALENT &&
      role === MESSAGE_ROLES.ASSISTANT
    ) {
      return <DimensionMatchMessage {...dimensionMatchMessageProps} />
    }

    if (
      type === MESSAGE_TYPES.DIMENSION_PREVIEW &&
      role === MESSAGE_ROLES.ASSISTANT
    ) {
      return <DimensionPreviewMessage />
    }

    if (
      type === MESSAGE_TYPES.INVITE_DEMO_TALENT &&
      role === MESSAGE_ROLES.ASSISTANT
    ) {
      return <InviteDemoTalentMessage onClick={onClick} />
    }

    if (
      type === MESSAGE_TYPES.COMPANY_PROFILE_ADOPTION &&
      role === MESSAGE_ROLES.ASSISTANT
    ) {
      return <CompanyProfileAdoptionMessage {...companyProfileAdoptionProps} />
    }

    if (type === MESSAGE_TYPES.NAME_INPUT && role === MESSAGE_ROLES.ASSISTANT) {
      return <NameInputMessage {...nameInputProps} />
    }

    if (type === MESSAGE_TYPES.TEXT && role === MESSAGE_ROLES.ASSISTANT) {
      return (
        <SystemMessage
          title={title}
          description={description}
          role={role}
          cta={cta}
          withQrCode={withQrCode}
          className={className}
          imageSrc={imageSrc}
        />
      )
    }

    if (type === MESSAGE_TYPES.CTA && role === MESSAGE_ROLES.ASSISTANT) {
      return (
        <CtaMessage description={description} cta={cta} className={className} />
      )
    }

    if (
      type === MESSAGE_TYPES.CATEGORY_SELECTION &&
      role === MESSAGE_ROLES.ASSISTANT
    ) {
      return <CategorySelectionMessage {...categorySelectionProps} />
    }

    if (type === MESSAGE_TYPES.OPTION && role === MESSAGE_ROLES.ASSISTANT) {
      return <OptionMessage options={options} />
    }

    return null
  }

  const component = getMessageComponent()

  return component ? (
    <motion.div
      key={id}
      className='rounded-xl bg-gray-50 p-1'
      {...MOTION_ANIMATION_PROPS}
    >
      {component}
    </motion.div>
  ) : null
}
